import { useTranslation } from "react-i18next"; // src/portal/SetupPassword.jsx
import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import api from '../services/api';
import toast from 'react-hot-toast';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';
import { useTheme } from '../contexts/ThemeContext';

const SetupPassword = () => {const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const type = searchParams.get('type');
  const id = searchParams.get('id');
  const email = searchParams.get('email');
  const token = searchParams.get('token');

  const [userEmail, setUserEmail] = useState(email || '');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);

  const { isDarkMode } = useTheme();
  const navigate = useNavigate();

  useEffect(() => {
    // email is no longer strictly required in the URL (frictionless flow)
    if (!type || !id || !token) {
      toast.error('Invalid or incomplete setup link');
      navigate(`/portal/${type || 'owner'}/login`);
    }
  }, [type, id, token, navigate]);

  const handleSetup = async (e) => {
    e.preventDefault();

    if (!userEmail) {
      return toast.error('Please enter your email address');
    }

    if (password !== confirmPassword) {
      return toast.error('Passwords do not match');
    }

    setLoading(true);
    try {
      const { data } = await api.post('/portal/auth/setup-password', {
        type, id, email: userEmail, password, token
      });

      if (data.success) {
        toast.success('Password set successfully. You can now login.');
        navigate(`/portal/${type}/login`);
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to setup password');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={`min-h-screen flex items-center justify-center p-4 transition-colors duration-300 ${isDarkMode ? 'bg-zinc-950' : 'bg-gray-50'}`}>
            <div className={`max-w-md w-full relative z-10 rounded-xl shadow-lg border p-6 sm:p-8 transition-colors duration-300 ${isDarkMode ? 'bg-zinc-900 border-zinc-800' : 'bg-white border-gray-200'}`}>
                <div className="text-center mb-8">
                    <div className="mb-6 flex justify-center">
                        <img
              src="https://quadcores.com/quadcores-logo1.png"
              alt="QuadCores Logo"
              className="h-16 w-auto" />
            
                    </div>
                    <h1 className={`text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                        {t("set_up_your_password")}
                    </h1>
                    <p className={`mt-2 font-medium text-sm ${isDarkMode ? 'text-zinc-400' : 'text-gray-500'}`}>
                        {email ?
            <>{t("create_a_strong_password_for")} <span className={isDarkMode ? 'text-zinc-200' : 'text-gray-900'}>{email}</span></> :

            'Set up your account and choose your login email'
            }
                    </p>
                </div>

                <form className="space-y-5" onSubmit={handleSetup}>
                    {/* Only show email field if it wasn't provided in the link */}
                    {!email &&
          <div>
                            <label className={`block text-sm font-medium mb-1.5 ${isDarkMode ? 'text-zinc-300' : 'text-gray-700'}`}>
                                {t("email_address_2")}
                            </label>
                            <input
              type="email"
              required
              value={userEmail}
              onChange={(e) => setUserEmail(e.target.value)}
              className={`w-full p-3 rounded-lg border transition-colors focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500 ${isDarkMode ? 'bg-zinc-800 border-zinc-700 text-white placeholder-zinc-500' : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400'}`}
              placeholder="Enter your login email" />
            
                        </div>
          }

                    <div>
                        <label className={`block text-sm font-medium mb-1.5 ${isDarkMode ? 'text-zinc-300' : 'text-gray-700'}`}>
                            {t("new_password_1")}
                        </label>
                        <input
              type="password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              minLength={8}
              className={`w-full p-3 rounded-lg border transition-colors focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500 ${isDarkMode ? 'bg-zinc-800 border-zinc-700 text-white placeholder-zinc-500' : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400'}`}
              placeholder="Minimum 8 characters" />
            
                    </div>

                    <div>
                        <label className={`block text-sm font-medium mb-1.5 ${isDarkMode ? 'text-zinc-300' : 'text-gray-700'}`}>
                            {t("confirm_password_1")}
                        </label>
                        <input
              type="password"
              required
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              minLength={8}
              className={`w-full p-3 rounded-lg border transition-colors focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500 ${isDarkMode ? 'bg-zinc-800 border-zinc-700 text-white placeholder-zinc-500' : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400'}`}
              placeholder="Re-enter password" />
            
                    </div>

                    <div className="pt-2">
                        <button
              type="submit"
              disabled={loading}
              className={`w-full py-3 px-4 rounded-lg bg-red-600 hover:bg-red-700 text-white font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 flex justify-center items-center disabled:opacity-50 disabled:cursor-not-allowed ${isDarkMode ? 'focus:ring-offset-zinc-900' : 'focus:ring-offset-white'}`}>
              
                            {loading ?
              <>
                                    <div className="h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                                    Setting Password...
                                </> :

              'Set Password & Login'
              }
                        </button>
                    </div>
                </form>

                <div className={`mt-8 pt-6 border-t text-center ${isDarkMode ? 'border-zinc-800' : 'border-gray-100'}`}>
                    <button
            type="button"
            onClick={() => navigate(`/portal/${type || 'owner'}/login`)}
            className={`inline-flex items-center text-sm font-medium transition-colors ${isDarkMode ? 'text-red-400 hover:text-red-300' : 'text-red-600 hover:text-red-700'}`}>
            
                        <ArrowLeftIcon className="w-4 h-4 mr-1.5" /> {t("back_to_login_1")}
                    </button>
                </div>
            </div>
        </div>);

};

export default SetupPassword;